import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import type { FilterValue, MenuCoords } from '../types';

type RefField = 'yearInitial' | 'yearFinal';

const COMPOSITE_OPTIONS: { field: RefField; operator: string; label: string }[] = [
  { field: 'yearInitial', operator: 'gt', label: 'Greater than (by firstRef)' },
  { field: 'yearInitial', operator: 'lt', label: 'Less than (by firstRef)' },
  { field: 'yearInitial', operator: 'eq', label: 'Equals (by firstRef)' },
  { field: 'yearInitial', operator: 'gte', label: 'Greater or equal (by firstRef)' },
  { field: 'yearInitial', operator: 'lte', label: 'Less or equal (by firstRef)' },
  { field: 'yearFinal', operator: 'gt', label: 'Greater than (by lastRef)' },
  { field: 'yearFinal', operator: 'lt', label: 'Less than (by lastRef)' },
  { field: 'yearFinal', operator: 'eq', label: 'Equals (by lastRef)' },
  { field: 'yearFinal', operator: 'gte', label: 'Greater or equal (by lastRef)' },
  { field: 'yearFinal', operator: 'lte', label: 'Less or equal (by lastRef)' },
];

function deriveCompositeKey(
  filterValues: Record<string, string>,
  filterOperators: Record<string, string>,
): string {
  const fr = filterValues.yearInitial?.trim();
  const lr = filterValues.yearFinal?.trim();
  const frOp = filterOperators.yearInitial || 'eq';
  const lrOp = filterOperators.yearFinal || 'eq';
  if (fr) return `yearInitial:${frOp}`;
  if (lr) return `yearFinal:${lrOp}`;
  if (lrOp !== 'eq') return `yearFinal:${lrOp}`;
  if (frOp !== 'eq') return `yearInitial:${frOp}`;
  return 'yearInitial:eq';
}

function parseCompositeKey(key: string): { field: RefField; operator: string } {
  const [field, op] = key.split(':');
  return { field: (field as RefField) || 'yearInitial', operator: op || 'eq' };
}

type EvidenceTimelineColumnFilterProps = {
  filterValues: Record<string, string>;
  filterOperators: Record<string, string>;
  onFilterChange?: (columnId: string, value: string) => void;
  onFilterOperatorChange?: (columnId: string, operator: string) => void;
  onBatchFilterChange?: (mutator: (filters: FilterValue[]) => FilterValue[]) => void;
};

export default function EvidenceTimelineColumnFilter({
  filterValues,
  filterOperators,
  onFilterChange,
  onFilterOperatorChange,
  onBatchFilterChange,
}: EvidenceTimelineColumnFilterProps) {
  const compositeKey = deriveCompositeKey(filterValues, filterOperators);
  const { field: activeField } = parseCompositeKey(compositeKey);
  const value = filterValues[activeField] ?? '';

  const [showOperatorMenu, setShowOperatorMenu] = useState(false);
  const [menuCoords, setMenuCoords] = useState<MenuCoords>({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (showOperatorMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceOnLeft = rect.left;
      const position = spaceOnLeft < 260 ? 'right' : 'left';
      setMenuCoords({
        top: rect.bottom + 4,
        left: position === 'left' ? rect.right - 280 : rect.left,
      });
    }
  }, [showOperatorMenu]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowOperatorMenu(false);
      }
    }
    function updatePosition() {
      if (showOperatorMenu && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceOnLeft = rect.left;
        const position = spaceOnLeft < 260 ? 'right' : 'left';
        setMenuCoords({
          top: rect.bottom + 4,
          left: position === 'left' ? rect.right - 280 : rect.left,
        });
      }
    }
    if (showOperatorMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showOperatorMenu]);

  const handleSelectOption = useCallback(
    (nextField: RefField, nextOperator: string) => {
      const sameField = nextField === activeField;
      if (sameField) {
        onFilterOperatorChange?.(nextField, nextOperator);
        setShowOperatorMenu(false);
        return;
      }
      const other: RefField = nextField === 'yearInitial' ? 'yearFinal' : 'yearInitial';
      if (onBatchFilterChange) {
        onBatchFilterChange(filters =>
          filters.map(f => {
            if (f.name === other) return { ...f, value: '', operator: 'eq' };
            if (f.name === nextField) return { ...f, operator: nextOperator };
            return f;
          }),
        );
      } else {
        onFilterChange?.(other, '');
        onFilterOperatorChange?.(nextField, nextOperator);
      }
      setShowOperatorMenu(false);
    },
    [activeField, onBatchFilterChange, onFilterChange, onFilterOperatorChange],
  );

  const handleValueChange = (v: string) => {
    onFilterChange?.(activeField, v);
  };

  const handleClear = () => {
    onFilterChange?.(activeField, '');
  };

  const renderMenu = () => {
    if (!showOperatorMenu) return null;
    return ReactDOM.createPortal(
      <div
        ref={menuRef}
        className="filter-operator-menu-portal"
        style={{
          position: 'fixed',
          top: `${menuCoords.top}px`,
          left: `${menuCoords.left}px`,
          zIndex: 99999,
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          minWidth: '280px',
          maxWidth: 'min(360px, calc(100vw - 24px))',
          overflow: 'hidden',
        }}
      >
        {COMPOSITE_OPTIONS.map(opt => {
          const optKey = `${opt.field}:${opt.operator}`;
          const isActive = optKey === compositeKey;
          return (
            <div
              key={optKey}
              className={`filter-operator-option ${isActive ? 'active' : ''}`}
              onClick={() => handleSelectOption(opt.field, opt.operator)}
            >
              {opt.label}
            </div>
          );
        })}
      </div>,
      document.body,
    );
  };

  return (
    <div className="column-filter" onClick={e => e.stopPropagation()}>
      <div className="filter-input-wrapper">
        <input
          type="number"
          value={value}
          onChange={e => handleValueChange(e.target.value)}
          placeholder="Filter…"
          className="filter-input"
          aria-label="Evidence timeline filter value"
        />
        {value ? (
          <button
            type="button"
            className="filter-clear-button"
            onClick={e => {
              e.stopPropagation();
              handleClear();
            }}
            title="Clear filter"
          >
            ×
          </button>
        ) : null}
        <div className="filter-menu-wrapper">
          <button
            ref={buttonRef}
            type="button"
            className="filter-menu-button"
            onClick={e => {
              e.stopPropagation();
              setShowOperatorMenu(!showOperatorMenu);
            }}
            title="Filter operator"
          >
            ⋮
          </button>
        </div>
      </div>
      {renderMenu()}
    </div>
  );
}
